/**
 * Theme utilities for dual-dimensional theme engine:
 * 1. Palette theme: "glass" (default) | "semantic" | "terracotta" (applied via data-theme on documentElement)
 * 2. Mode: "light" | "dark" | "system" (applied via class "dark" on documentElement)
 */

export const PALETTE_THEME_STORAGE_KEY = "homy-palette-theme";
export const MODE_STORAGE_KEY = "homy-theme-mode";
export const THEME_STORAGE_KEY = "homy-theme"; // backward compatibility key

export const PALETTE_THEMES = Object.freeze({
  glass: "glass",
  semantic: "semantic",
  terracotta: "terracotta",
});

export const DEFAULT_PALETTE_THEME = PALETTE_THEMES.terracotta;

export const MODES = Object.freeze({
  light: "light",
  dark: "dark",
  system: "system",
});

export const DEFAULT_MODE = MODES.system;

// Backward-compatible themes dictionary containing both modes and palette themes
export const themes = Object.freeze({
  light: "light",
  dark: "dark",
  system: "system",
  glass: "glass",
  semantic: "semantic",
  terracotta: "terracotta",
});

/**
 * Returns system preference from OS media query
 */
export function getSystemTheme() {
  if (typeof window === "undefined" || !window.matchMedia) return MODES.light;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? MODES.dark
    : MODES.light;
}

/**
 * Resolves effective mode ("light" | "dark") from mode preference
 */
export function resolveMode(mode, prefersDark = null) {
  let selected = DEFAULT_MODE;
  if (mode && Object.values(MODES).includes(mode)) {
    selected = mode;
  }

  if (selected === MODES.system) {
    const isDark =
      prefersDark !== null
        ? prefersDark
        : getSystemTheme() === MODES.dark;
    return {
      selectedMode: MODES.system,
      resolvedMode: isDark ? MODES.dark : MODES.light,
    };
  }

  return {
    selectedMode: selected,
    resolvedMode: selected === MODES.dark ? MODES.dark : MODES.light,
  };
}

/**
 * Resolves effective theme ("light" | "dark") from theme preference
 * Maintained for backward compatibility.
 */
export function resolveTheme(theme, prefersDark = null) {
  if (theme && Object.values(PALETTE_THEMES).includes(theme)) {
    return {
      selectedTheme: theme,
      resolvedMode: resolveMode(getMode(), prefersDark).resolvedMode,
    };
  }
  const { selectedMode, resolvedMode } = resolveMode(theme, prefersDark);
  return {
    selectedTheme: selectedMode,
    resolvedMode,
  };
}

/**
 * Get the currently configured palette theme preference.
 * Defaults to "glass".
 */
export function getPaletteTheme() {
  if (typeof window === "undefined") return DEFAULT_PALETTE_THEME;

  try {
    const stored =
      localStorage.getItem(PALETTE_THEME_STORAGE_KEY) ||
      localStorage.getItem("homy_theme") ||
      localStorage.getItem("homy-active-theme") ||
      localStorage.getItem("homy_palette_theme");
    if (stored && Object.values(PALETTE_THEMES).includes(stored)) {
      return stored;
    }
  } catch (_e) {
    return DEFAULT_PALETTE_THEME;
  }

  return DEFAULT_PALETTE_THEME;
}

/**
 * Get the currently configured mode preference ("light" | "dark" | "system").
 * Defaults to "system".
 */
export function getMode() {
  if (typeof window === "undefined") return DEFAULT_MODE;

  try {
    const stored =
      localStorage.getItem(MODE_STORAGE_KEY) ||
      localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && Object.values(MODES).includes(stored)) {
      return stored;
    }
  } catch (_e) {
    return DEFAULT_MODE;
  }

  return DEFAULT_MODE;
}

/**
 * Backward-compatible getTheme(): returns current mode preference for callers
 * expecting "light" | "dark" | "system" (e.g. AppLayout.jsx).
 */
export function getTheme() {
  return getMode();
}

/**
 * Applies palette theme attribute ("data-theme") directly to documentElement
 */
export function applyPaletteThemeToDom(paletteTheme) {
  if (typeof window === "undefined" || !document?.documentElement) return;
  const root = document.documentElement;
  const valid = Object.values(PALETTE_THEMES).includes(paletteTheme)
    ? paletteTheme
    : DEFAULT_PALETTE_THEME;
  root.setAttribute("data-theme", valid);
}

/**
 * Applies resolved mode class ("dark") directly to documentElement
 */
export function applyModeToDom(resolvedMode) {
  if (typeof window === "undefined" || !document?.documentElement) return;
  const root = document.documentElement;
  if (resolvedMode === MODES.dark) {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

/**
 * Backward-compatible applyThemeToDom(): applies resolved mode class.
 */
export function applyThemeToDom(resolvedMode) {
  applyModeToDom(resolvedMode);
}

/**
 * Set and persist palette theme
 */
export function setPaletteTheme(paletteTheme) {
  if (typeof window === "undefined") return;

  const valid = Object.values(PALETTE_THEMES).includes(paletteTheme)
    ? paletteTheme
    : DEFAULT_PALETTE_THEME;

  try {
    localStorage.setItem(PALETTE_THEME_STORAGE_KEY, valid);
    localStorage.setItem("homy_theme", valid);
    localStorage.setItem("homy-active-theme", valid);
  } catch (_e) {
    // Ignore storage write errors (e.g., SecurityError in sandboxed browsing)
  }

  applyPaletteThemeToDom(valid);
}

/**
 * Set and persist mode ("light" | "dark" | "system")
 */
export function setMode(mode) {
  if (typeof window === "undefined") return;

  const valid = Object.values(MODES).includes(mode) ? mode : DEFAULT_MODE;

  try {
    localStorage.setItem(MODE_STORAGE_KEY, valid);
    localStorage.setItem(THEME_STORAGE_KEY, valid);
  } catch (_e) {
    // Ignore storage write errors
  }

  const { resolvedMode } = resolveMode(valid);
  applyModeToDom(resolvedMode);
}

/**
 * Overloaded setTheme function for backward compatibility:
 * - If called with "light", "dark", or "system", sets mode.
 * - If called with "glass", "semantic", or "terracotta", sets palette theme.
 */
export function setTheme(themeOrMode) {
  if (Object.values(PALETTE_THEMES).includes(themeOrMode)) {
    setPaletteTheme(themeOrMode);
  } else {
    setMode(themeOrMode);
  }
}

/**
 * Initialize theme on page load and attach dynamic OS media query listener
 */
export function initTheme() {
  if (typeof window === "undefined") return;

  // 1. Initialize and apply palette theme
  const currentPalette = getPaletteTheme();
  applyPaletteThemeToDom(currentPalette);

  // 2. Initialize and apply mode
  const currentMode = getMode();
  const { resolvedMode } = resolveMode(currentMode);
  applyModeToDom(resolvedMode);

  // 3. Dynamic listener for OS color scheme switches
  if (!window.matchMedia) return;
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const handleMediaChange = (e) => {
    const active = getMode();
    if (active === MODES.system) {
      applyModeToDom(e.matches ? MODES.dark : MODES.light);
    }
  };

  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener("change", handleMediaChange);
  } else if (mediaQuery.addListener) {
    mediaQuery.addListener(handleMediaChange);
  }
}

